/**
 * The 5-department architecture that drives the top navigation bar.
 *
 * Every department starts with its dashboard entry (Control Panel) followed by
 * its department-specific headers. The old "Rentals" area is split in two:
 * SALES looks after the current machines, customers and contract creation;
 * OPERATIONS runs the machines in the field (sites, machine contracts, errors,
 * log sheets). FINANCIAL is the Analysis page (+ Billing). MARKETING is being
 * built next — it appears (disabled) for the super admin only.
 *
 * Paths may carry a query (?view= / ?cat=) to deep-link a card inside
 * RentalPage / ServicePage. Access is still ruled by utils/permissions.js on
 * the PATHNAME — a header shows only if the user can open that page, so this
 * file never loosens a permission. Icon names resolve in Sidebar's ICONS map.
 *
 * user.department (from the JWT) only ORDERS the bar (your own departments
 * first) and never grants access by itself.
 */

export const DEPARTMENTS = [
    {
        key: 'service',
        label: 'Service',
        // Pages reached by CLICKING inside these headers, which have no header
        // of their own: a machine's service detail and its telematics detail
        // (the latter is where a CAN error in the Daily Report opens). Without
        // these a service engineer clicking a machine in the equipment list — or
        // an error in the report — is bounced back to the Control Panel.
        grants: ['servicemachine', 'machinedetail'],
        // Every header mounts the department's OWN page file under
        // client/src/components/pages/service/ — see App.jsx.
        items: [
            // Service has its OWN control panel (its pipeline, not the fleet's).
            { text: 'Control Panel', path: '/service/control-panel', icon: 'controlpanel' },
            { text: 'Equipment', path: '/service/equipment', icon: 'equipment' },
            { text: 'Daily Report', path: '/service/daily-report', icon: 'logsheets' },
            { text: 'Maintenance', path: '/service/maintenance', icon: 'maintenance' },
            { text: 'Errors', path: '/service/errors', icon: 'errors' },    // device-reported, from the field
            // One machine at a time: active faults + thresholds, the check
            // sheet, what happened since the last service, parts changed.
            { text: 'Diagnosis', path: '/service/diagnosis', icon: 'diagnosis' },
            { text: 'Job Cards', path: '/service/job-cards', icon: 'jobcards' },
            // The OEM parts manuals: an assembly's exploded drawing beside its
            // parts table, so an engineer can name the part they are holding.
            { text: 'Parts Catalogue', path: '/service/parts-catalog', icon: 'partscatalog' },
            // The hardware workshop: the service team installs the TCUs, so
            // register → test (receiving-data checks) → install lives here too.
            { text: 'Hardware Installation', path: '/service/hardware', icon: 'hardware' },
        ],
    },
    {
        key: 'sales',
        label: 'Sales',
        // Detail pages reached by clicking inside the sales pages: a contract,
        // a customer's rentals, a machine.
        grants: ['rentals', 'machinedetail'],
        // Department-owned files under pages/sales/ — see App.jsx.
        items: [
            { text: 'Control Panel', path: '/sales/control-panel', icon: 'controlpanel' },
            { text: 'Contracts', path: '/sales/contracts', icon: 'contracts' },
            { text: 'Customers', path: '/sales/customers', icon: 'customers' },
            { text: 'Equipments', path: '/sales/equipment', icon: 'equipment' },
        ],
    },
    {
        key: 'operations',
        label: 'Operations',
        // A machine opened from the Equipments list or a Log Sheets report row,
        // and the contract detail pages opened from Machine Contracts.
        grants: ['machinedetail', 'rentals'],
        // Department-owned files under pages/operations/ — see App.jsx.
        items: [
            { text: 'Control Panel', path: '/operations/control-panel', icon: 'controlpanel' },
            // The day's ran / did-not-run / moved / overtime story. Invoices
            // live on Log Sheets, not here.
            { text: 'Daily Report', path: '/operations/daily-report', icon: 'logsheets' },
            // The equipment engine in its Operations guise: last engine-on /
            // last GPS-on instead of last-seen, with each machine's shift window.
            { text: 'Equipments', path: '/operations/equipment', icon: 'equipment' },
            { text: 'Sites', path: '/operations/sites', icon: 'sites' },
            { text: 'Machine Contracts', path: '/operations/machine-contracts', icon: 'contracts' },
            { text: 'Errors', path: '/operations/errors', icon: 'errors' },
            // Automatic issue raising: silent machines, serious faults and
            // hand-raised issues go supervisor → manager → head until answered.
            { text: 'Issues', path: '/operations/issues', icon: 'issues' },
            // Is each machine actually SENDING its engine / angle / load data?
            // Errors shows what a machine complained about; this shows what it
            // never said at all — a dead sensor raises no error.
            { text: 'Signal Coverage', path: '/operations/signal-coverage', icon: 'signalcoverage' },
            // Two tabs: the invoice downloader, and the Weekly Data Check the
            // team runs before those go out (?tab=check).
            { text: 'Log Sheets', path: '/operations/log-sheets', icon: 'logsheets' },
        ],
    },
    {
        key: 'financial',
        label: 'Financial',
        // Deep links inside Analysis/Billing keep working for financial users.
        grants: ['analysis', 'billing'],
        // Department-owned files under pages/financial/ — see App.jsx.
        items: [
            { text: 'Analysis', path: '/financial/analysis', icon: 'analysis' },
            { text: 'Billing', path: '/financial/billing', icon: 'billing' },
        ],
    },
    {
        key: 'marketing',
        label: 'Marketing',
        comingSoon: true,                                   // built next
        items: [],
    },
];

/**
 * Everything that is not a department header, in the trailing groups — the
 * one long "Workspace" run was a scroll of unrelated icons, so it is split by
 * what the page is FOR:
 *
 *   SUPER ADMIN  the fleet-wide master lists the admin owns (the TCU register
 *                and the hardware workshop). Named for who works here, not for
 *                what it contains — the pages under it are the ones no single
 *                department owns.
 *   INVENTORY    holding and buying parts: control panel, stock, catalogue, POs.
 *   HR           the people side: the org, the mailboxes, chat, tasks.
 *   CONFIG       the plumbing behind the fleet: TCU configuration, the
 *                Sinoboom i-Link credentials, the debug console, SIM SMS.
 *
 * `defaultOpen` keeps each of these showing its pages for the super admin
 * until they minimize it themselves: they are short runs, unlike the 14-item
 * Workspace scroll they replace, which had to start collapsed to be readable.
 *
 * These are GROUPING only. Access is still ruled per item by
 * utils/permissions.js on the pathname exactly as before, so a group simply
 * does not appear when the user can open none of its pages, and no group key
 * here ever grants anything.
 */
export const WORKSPACE_GROUPS = [
    {
        key: 'superadmin',
        label: 'Super Admin',
        defaultOpen: true,
        items: [
            { text: 'Equipment', path: '/equipment', icon: 'devices' },
            { text: 'Hardware', path: '/hardware', icon: 'hardware' },
        ],
    },
    {
        key: 'procurement',
        label: 'Inventory',
        defaultOpen: true,
        items: [
            { text: 'Control Panel', path: '/inventory/control-panel', icon: 'controlpanel' },
            { text: 'Inventory', path: '/inventory', icon: 'inventory' },
            { text: 'Parts Catalogue', path: '/parts-catalog', icon: 'partscatalog' },
            { text: 'Procurement', path: '/procurement', icon: 'procurement' },
        ],
    },
    {
        key: 'hr',
        label: 'HR',
        defaultOpen: true,
        items: [
            { text: 'Organization', path: '/organization', icon: 'organization' },
            { text: 'Mail', path: '/mail', icon: 'mail' },
            { text: 'Mail Intake', path: '/email-intake', icon: 'mailintake' },
            { text: 'Chat', path: '/chat', icon: 'chat' },
            { text: 'Tasks', path: '/tasks', icon: 'tasks' },
        ],
    },
    {
        key: 'config',
        label: 'Config',
        defaultOpen: true,
        items: [
            { text: 'Control Panel', path: '/device-config/control-panel', icon: 'controlpanel' },
            { text: 'Device Config', path: '/device-config', icon: 'deviceconfig' },
            { text: 'Sinoboom i-Link', path: '/sinoboom-settings', icon: 'hardware' },
            { text: 'Debug Console', path: '/debug', icon: 'debug' },
            { text: 'M2M SMS', path: '/sms', icon: 'sms' },
        ],
    },
];

/**
 * Every non-department page as one flat list — the four groups above joined
 * back together. Nothing may be added to a group without appearing here, so
 * this stays the single answer to "is this page reachable from the bar?".
 */
export const WORKSPACE = {
    key: 'workspace',
    label: 'Workspace',
    items: WORKSPACE_GROUPS.flatMap(g => g.items),
};

/** The pathname part of a nav path that may carry a ?query. */
export const pathnameOf = (p) => String(p || '').split('?')[0];

/** Is the browser on this item's page (ignoring any ?query the item carries)? */
export const isItemOnPath = (location, itemPath) => {
    const p = pathnameOf(itemPath);
    return location.pathname === p || (p !== '/' && location.pathname.startsWith(p + '/'));
};

/**
 * Is this exact header active? Items that deep-link a card (?view= / ?cat=)
 * are active only when their query matches, so the three headers sharing
 * /rentals never light up together.
 */
export const isItemActive = (location, itemPath) => {
    if (!isItemOnPath(location, itemPath)) return false;
    const q = String(itemPath).split('?')[1];
    if (!q) return true;
    const want = new URLSearchParams(q);
    const have = new URLSearchParams(location.search);
    for (const [k, v] of want.entries()) {
        if (have.get(k) !== v) return false;
    }
    return true;
};

/**
 * Which departments the user's profile `department` names (ordering only).
 * Legacy "rental" departments map to BOTH halves of the split.
 */
export const userDeptKeys = (user) => {
    const d = String(user?.department || '').toLowerCase();
    const keys = [];
    if (/service/.test(d)) keys.push('service');
    if (/sales/.test(d)) keys.push('sales');
    if (/operat|ops/.test(d)) keys.push('operations');
    if (/rental/.test(d)) keys.push('sales', 'operations');
    if (/financ|account/.test(d)) keys.push('financial');
    if (/market/.test(d)) keys.push('marketing');
    return keys;
};
